import { useRef, useState } from 'react';
import { SectionCard } from './section-card';
import { useAuth } from '@/hooks/use-auth';
import { uploadAvatar } from '@/lib/settings-api';
import { resolveAvatarUrl } from '@/lib/avatar-url';

interface ProfileSectionProps {
  name: string;
  nickname: string;
  avatarUrl: string;
  onSaved: () => void;
}

export function ProfileSection({ name, nickname, avatarUrl, onSaved }: ProfileSectionProps) {
  const { accessToken, patchProfile, reloadProfile } = useAuth();
  const [nameVal, setNameVal] = useState(name);
  const [nickVal, setNickVal] = useState(nickname);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(resolveAvatarUrl(avatarUrl));
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleSave() {
    setStatus('saving');
    setError('');
    try {
      await patchProfile({ name: nameVal.trim(), nickname: nickVal.trim() });
      setStatus('saved');
      onSaved();
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !accessToken) return;
    setStatus('saving');
    setError('');
    try {
      const { avatar_url } = await uploadAvatar(accessToken, file);
      setPreview(resolveAvatarUrl(avatar_url));
      await reloadProfile();
      setStatus('saved');
      onSaved();
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    }
  }

  const initials = (nickVal || nameVal || '?').slice(0, 2).toUpperCase();

  return (
    <SectionCard title="Profil" description="Dein Name, Nickname und Avatar.">
      <div className="flex items-center gap-4">
        {preview ? (
          <img src={preview} alt="Avatar" className="size-16 rounded-full object-cover" />
        ) : (
          <div className="flex size-16 items-center justify-center rounded-full bg-amber-500/20 text-lg font-semibold text-amber-300">
            {initials}
          </div>
        )}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="rounded-lg border border-slate-600 px-3 py-1.5 text-sm text-slate-200 hover:border-amber-400"
        >
          Avatar ändern
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleFile}
        />
      </div>

      <label className="block text-sm text-slate-300">
        Name
        <input
          value={nameVal}
          onChange={(e) => setNameVal(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-slate-100"
        />
      </label>
      <label className="block text-sm text-slate-300">
        Nickname (so spricht EVE dich an)
        <input
          value={nickVal}
          onChange={(e) => setNickVal(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-slate-100"
        />
      </label>

      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      <button
        type="button"
        onClick={handleSave}
        disabled={status === 'saving'}
        className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
      >
        {status === 'saving' ? 'Speichert…' : status === 'saved' ? 'Gespeichert ✓' : 'Speichern'}
      </button>
    </SectionCard>
  );
}
